import React from "react";
import {
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import MicOutlined from "@mui/icons-material/MicOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import LibraryMusicOutlined from "@mui/icons-material/LibraryMusicOutlined";
import CharacterVoicePickerModal from "./CharacterVoicePickerModal";
import { characterLabelFromUpload } from "../modelProfiles";

const VOICE_ACCEPT =
  "audio/*,audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/m4a,audio/x-m4a,audio/aac,audio/webm,.mp3,.wav,.ogg,.m4a,.aac,.webm";
const VOICE_MAX_BYTES = 20 * 1024 * 1024;

function isVoiceFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  if (type.startsWith("audio/")) return true;
  const name = String(file.name || "").toLowerCase();
  return /\.(mp3|wav|ogg|m4a|aac|webm)$/i.test(name);
}

/**
 * Image thumbnail with an optional voice clip attached underneath.
 * Mic opens: upload sample OR choose from Voices library.
 * Chip label prefers the character / @tag name (Amara), not the library voice name (Jessa).
 */
export default function CharacterImageTile({ image, onChange, onRemove, disabled, lang }) {
  const theme = useTheme();
  const voiceInputRef = React.useRef(null);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const preview = image.previewUrl || "";
  const voiceClip = image.voiceClip || null;
  const characterName = characterLabelFromUpload(image);
  const voiceName =
    voiceClip?.name || voiceClip?.file?.name || voiceClip?.voiceId || "";
  const chipLabel = characterName || voiceName;
  const chipTitle =
    characterName && voiceName && characterName !== voiceName
      ? `${characterName} · ${voiceName}`
      : chipLabel;

  const handleVoiceSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > VOICE_MAX_BYTES) {
      window.alert?.(
        lang?.videoStudioVoiceTooLarge || "Voice file is too large (max 20MB).",
      );
      e.target.value = "";
      return;
    }
    if (!isVoiceFile(file)) {
      window.alert?.(
        lang?.videoStudioVoiceTypeInvalid ||
          "Please upload an audio file (MP3, WAV, M4A, OGG, or AAC).",
      );
      e.target.value = "";
      return;
    }
    if (voiceClip?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(voiceClip.previewUrl);
    }
    onChange({
      ...image,
      voiceClip: {
        id: `voice-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        name: file.name,
        source: "upload",
      },
    });
    e.target.value = "";
  };

  const applyLibraryVoice = (voice) => {
    if (!voice?.voice_id) return;
    if (voiceClip?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(voiceClip.previewUrl);
    }
    onChange({
      ...image,
      // Keep characterName / filename as the @tag identity — do not rename to the voice.
      voiceClip: {
        id: `voice-lib-${voice.voice_id}`,
        voiceId: String(voice.voice_id),
        name: String(voice.name || voice.voice_id),
        previewUrl: voice.preview_url || null,
        source: "library",
      },
    });
    setPickerOpen(false);
  };

  const removeVoice = (e) => {
    e.stopPropagation();
    if (voiceClip?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(voiceClip.previewUrl);
    }
    onChange({ ...image, voiceClip: null });
  };

  const openMenu = (e) => {
    if (disabled) return;
    setMenuAnchor(e.currentTarget);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 104,
          height: 120,
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          position: "relative",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {!disabled && (
          <IconButton
            size="small"
            onClick={onRemove}
            aria-label={lang?.remove || "Remove"}
            sx={{
              position: "absolute",
              top: 3,
              right: 3,
              zIndex: 2,
              width: 22,
              height: 22,
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <CloseOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        )}
        <Box
          component="img"
          src={preview}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {voiceClip ? (
        <Tooltip title={chipTitle}>
          <Chip
            size="small"
            icon={<MicOutlined sx={{ fontSize: 14 }} />}
            label={chipLabel.length > 12 ? `${chipLabel.slice(0, 10)}…` : chipLabel}
            onClick={disabled ? undefined : openMenu}
            onDelete={disabled ? undefined : removeVoice}
            deleteIcon={<CloseOutlined sx={{ fontSize: 12 }} />}
            sx={{
              maxWidth: 104,
              height: 24,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              cursor: disabled ? "default" : "pointer",
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip
          title={
            lang?.videoStudioAttachVoice ||
            "Upload a voice sample or choose a voice from your library"
          }
        >
          <IconButton
            size="small"
            disabled={disabled}
            onClick={openMenu}
            aria-label={lang?.videoStudioAttachVoice || "Attach voice"}
            sx={{
              width: 28,
              height: 28,
              border: "1px dashed",
              borderColor: alpha(theme.palette.text.primary, 0.25),
              borderRadius: 1.5,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            <MicOutlined sx={{ fontSize: 15, color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            voiceInputRef.current?.click();
          }}
        >
          <ListItemIcon>
            <UploadFileOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang?.videoStudioUploadVoice || "Upload voice"}
            secondary={lang?.videoStudioUploadVoiceHint || "MP3, WAV, M4A…"}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setPickerOpen(true);
          }}
        >
          <ListItemIcon>
            <LibraryMusicOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang?.videoStudioChooseFromVoices || "Choose from Voices"}
            secondary={lang?.videoStudioChooseFromVoicesHint || "My Voices & library"}
          />
        </MenuItem>
      </Menu>

      <input
        ref={voiceInputRef}
        type="file"
        accept={VOICE_ACCEPT}
        hidden
        onChange={handleVoiceSelect}
      />

      <CharacterVoicePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={applyLibraryVoice}
        lang={lang}
        value={
          voiceClip?.voiceId
            ? { voice_id: voiceClip.voiceId, name: voiceClip.name }
            : null
        }
      />
    </Box>
  );
}
